/**
 * The Overview map's watershed choropleth — the pure parts: the join, the classification
 * and the tooltip sentence.
 *
 * Everything here is a function of the `/activities/points` and `/hierarchy/units` payloads
 * and nothing else — no Leaflet, no React, no i18next instance. That is what makes the map's
 * arithmetic testable at all: the alternative is asserting a shading class by reading a fill
 * attribute back out of the DOM, which tests Leaflet rather than the number.
 *
 * The one rule that runs through it: **a number on this page must be one the reader can
 * check against the map beside it.** The eleven shaded basins add up to the registry minus
 * the `unassigned` the payload names out loud.
 *
 * The §F4 splash's other three helpers — `donutSlices`, `activityTotal` and `countryBars`,
 * which shaped the status donut, its centre number and the by-country bars — went with the
 * 50/50 card. S2 enumerates the insight panel's three panels and none of them is a donut.
 */
import type { ActivityPointsFC, UnitsFC } from '../../lib/api';
import { COUNTRY_ORDER, UNKNOWN_SERIES_COLOR } from '../../lib/palette';
import type { Country } from '../../lib/state';
import { classOf, classify, FALLBACK_RAMP_STOPS } from '../../lib/symbology';

/**
 * `"{n} activities · {watershed} · {country}"`, assembled from already-translated pieces.
 *
 * Takes the finished strings rather than an i18n instance so it stays pure: the caller has
 * the `t()` and the locale-aware number formatter, and this decides only the order and the
 * separators (a middle dot, as the mockup's tooltips use). Unchanged by R2 — the review
 * asked for the same sentence about a basin that §F4 wrote about a commune.
 */
export function markerTooltip(countLabel: string, unitName: string, countryName: string): string {
  return [countLabel, unitName, countryName].filter(Boolean).join(' · ');
}

/** ISO3 of a shaded unit, as a store `Country` — what clicking it sets. */
export function markerCountry(props: { country?: string | null }): Country | null {
  const code = props.country;
  if (!code) return null;
  return (COUNTRY_ORDER as readonly string[]).includes(code) ? (code as Country) : null;
}

/* ------------------------------------------------ R2: the splash choropleth by watershed */

/**
 * The mini-map's class count.
 *
 * Five, the app-wide §G1 default — but over **eleven** polygons, which is the reason it is
 * a constant here rather than the store's `indStyle.k`: the splash is not the Map Explorer
 * and has no style panel, and eleven basins split into five quantile classes is already
 * two shapes a class.
 */
export const WATERSHED_CLASSES = 5;

/**
 * The fill for a watershed with **no** activities.
 *
 * A real class, not a hole: `/activities/points?by=watershed` deliberately sends every
 * watershed including the empty ones, because "we work in nine of the eleven" is a fact the
 * splash should be able to show. Today all eleven are non-zero, so this paints nothing —
 * which is exactly when a no-data colour has to already be right.
 */
export const WATERSHED_NODATA_FILL = '#DCE4D8';

/**
 * The ramp. `FALLBACK_RAMP_STOPS` is the mockup's brand green, and the splash uses it
 * *directly* rather than resolving `/meta/config.ramps` the way §F5.3 does: the mini-map
 * has no indicator, no domain and no style panel, so `autoRamp` has nothing to key on, and
 * one more request on a first paint to fetch a ramp that is already the constant would be
 * a round trip for zero difference.
 */
export const WATERSHED_RAMP = FALLBACK_RAMP_STOPS;

export interface WatershedShading {
  /** `k - 1` upper bounds over the **non-zero** counts (§G1 quantiles). */
  breaks: number[];
  /** `k` fills, index-aligned with `classOf`. */
  colors: string[];
  /** Smallest and largest non-zero count in scope — the legend strip's two labels. */
  min: number;
  max: number;
  /** False when nothing in scope has an activity: everything paints as no-data. */
  hasData: boolean;
}

/**
 * Classify the watershed counts — §G1 quantiles over the same helper the Map Explorer's
 * choropleth uses, so the splash and the app cannot disagree about what a quantile is.
 *
 * **Zeroes are excluded from the classification, not from the map.** A basin with no
 * activity is not "the lowest class", it is a different statement, and leaving it in the
 * input would drag a break down to 0 and hand the palest green to basins that do have
 * activities.
 */
export function watershedShading(
  counts: readonly number[],
  k: number = WATERSHED_CLASSES,
  rampStops: string[] = WATERSHED_RAMP,
): WatershedShading {
  const positive = counts.filter((c) => Number.isFinite(c) && c > 0);
  const style = { ramp: null, method: 'quantile' as const, k, opacity: 1 };
  if (positive.length === 0) {
    return { ...classify([0], style, rampStops), breaks: [], min: 0, max: 0, hasData: false };
  }
  return {
    ...classify([...positive], style, rampStops),
    min: Math.min(...positive),
    max: Math.max(...positive),
    hasData: true,
  };
}

/**
 * One basin's fill: its quantile class, or the no-data wash when it has no activities.
 *
 * The `min === max` branch is the single-country scope, and it is not cosmetic. Quantiles
 * over one distinct value are degenerate — every break equals that value, `classOf` returns
 * 0, and the sole basin gets `#EFF6EE`, a green so pale it is the colour a reader has just
 * been told means *no activities*. A scale of one is not a scale, so it is drawn at full
 * strength and the legend prints the same number at both ends of the strip, which is the
 * honest description of that map.
 */
export function watershedFill(count: number, shading: WatershedShading): string {
  if (!(count > 0)) return WATERSHED_NODATA_FILL;
  if (shading.min === shading.max) return shading.colors.at(-1) ?? UNKNOWN_SERIES_COLOR;
  return shading.colors[classOf(count, shading.breaks)] ?? UNKNOWN_SERIES_COLOR;
}

export interface WatershedActivity {
  count: number;
  /**
   * Where the count label sits — the centroid `/activities/points?by=watershed` rides
   * along with each feature. `null` when the server had none, and then the basin is shaded
   * and tooltipped but carries no printed number, which is better than guessing a point
   * inside a multipolygon on the client.
   */
  centroid: [number, number] | null;
}

/**
 * Index the counts payload by `unit_id`, which is the join key against
 * `/hierarchy/units?type=watershed` — the two endpoints are deliberately split (the counts
 * carry no polygons; repeating eleven geometries per splash render is 29 kB thrown away),
 * so *something* has to do the join and it may as well be testable.
 */
export function watershedActivity(
  points: ActivityPointsFC | undefined,
): Map<string, WatershedActivity> {
  const out = new Map<string, WatershedActivity>();
  for (const f of points?.features ?? []) {
    const id = f.properties?.unit_id;
    if (!id) continue;
    const coords = f.geometry?.coordinates;
    out.set(id, {
      count: Number(f.properties.count) || 0,
      centroid:
        Array.isArray(coords) && coords.length === 2 && coords.every(Number.isFinite)
          ? [coords[0], coords[1]]
          : null,
    });
  }
  return out;
}

export interface WatershedRow {
  unit_id: string;
  name: string;
  country: string | null;
  count: number;
  centroid: [number, number] | null;
}

/**
 * The rows the mini-map draws: **geometry first**.
 *
 * The iteration is over `/hierarchy/units`, not over the counts, because the polygon is the
 * mark — a watershed the counts payload has never heard of still has a shape and still
 * belongs on the map (as no-data), while a count with no polygon has nowhere to go and is
 * dropped. Ordered biggest-first so the label of a busy basin is added last and wins the
 * overlap.
 */
export function watershedRows(
  units: UnitsFC | undefined,
  activity: Map<string, WatershedActivity>,
): WatershedRow[] {
  const rows: WatershedRow[] = [];
  for (const f of units?.features ?? []) {
    const p = f.properties;
    if (!p?.id || !f.geometry) continue;
    const hit = activity.get(p.id);
    rows.push({
      unit_id: p.id,
      name: p.name,
      country: p.country ?? null,
      count: hit?.count ?? 0,
      centroid: hit?.centroid ?? null,
    });
  }
  return rows.sort((a, b) => b.count - a.count || a.unit_id.localeCompare(b.unit_id));
}
